import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    describeImportReason,
    summarizeAppliedImport,
    summarizeClosedForecastAmendment,
} from './budgetSheetImportReasons';
import { ROUTES } from '../../consts/general';
import {
    SheetImportFooter,
    SheetImportHistory,
    SheetImportOverlay,
    SheetImportSuccessBanner,
    SheetImportUrlTabs,
} from '../sheetImport/SheetImportParts';

const date = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return `${String(parsed.getMonth() + 1).padStart(2, '0')}/${String(parsed.getDate()).padStart(2, '0')}/${parsed.getFullYear()}`;
};

const readDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    return `${date(value)} ${hours}:${minutes}`;
};

const amount = (value) => value == null
    ? '—'
    : Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const signedAmount = (value) => {
    if (value == null) return '—';
    const numeric = Number(value);
    const sign = numeric > 0 ? '+' : numeric < 0 ? '−' : '';
    return `${sign}${amount(Math.abs(numeric))}`;
};

const unmappedSheetAccountLabel = (item) => {
    const accountNumber = String(item.account_number || '').trim();
    const label = String(item.label || '').trim();
    return label.startsWith(accountNumber) ? label : [accountNumber, label].filter(Boolean).join(' · ');
};

const errorMessage = (error) => error.response?.data?.error || error.message || 'Budget Sheet import failed';

const dateLabel = (month) => {
    if (!month) return '—';
    return `${month.slice(5, 7)}/01/${month.slice(0, 4)}`;
};

const monthList = (months) => (months || []).map(dateLabel).join(', ') || 'None';

const percent = (value) => `${(Number(value) * 100).toLocaleString('en-US', {
    maximumFractionDigits: 4,
})}%`;

const BudgetSheetImportModal = ({
    budgetId,
    isOpen,
    canEdit,
    onClose,
    onApplied,
}) => {
    const navigate = useNavigate();
    const [url, setUrl] = useState('');
    const [tabs, setTabs] = useState([]);
    const [tab, setTab] = useState('');
    const [history, setHistory] = useState([]);
    const [preview, setPreview] = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [budgetRefreshStatus, setBudgetRefreshStatus] = useState(null);
    const [acknowledgeBlankCells, setAcknowledgeBlankCells] = useState(false);
    const [changedOnly, setChangedOnly] = useState(true);
    const [loadingTabs, setLoadingTabs] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const applySummaryRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (applyResult) {
            applySummaryRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        }
    }, [applyResult]);

    const fetchTabs = useCallback(async (targetUrl, preferredTab = null) => {
        setLoadingTabs(true);
        setError('');
        try {
            const { data } = await axios.get(`/api/v1/budget/${budgetId}/sheet-import/tabs`, {
                params: { url: targetUrl },
            });
            setTabs(data.tabs || []);
            const preferred = (data.tabs || []).find((item) => item.title === '2026 Budget - Governed')
                || (data.tabs || []).find((item) => item.title === preferredTab)
                || (data.tabs || [])[0];
            setTab(preferred?.title || '');
        } catch (requestError) {
            setError(errorMessage(requestError));
            setTabs([]);
            setTab('');
        } finally {
            setLoadingTabs(false);
        }
    }, [budgetId]);

    useEffect(() => {
        if (!isOpen || !budgetId) return;
        let active = true;
        const load = async () => {
            setError('');
            setPreview(null);
            setApplyResult(null);
            setBudgetRefreshStatus(null);
            setAcknowledgeBlankCells(false);
            try {
                const { data } = await axios.get(`/api/v1/budget/${budgetId}/sheet-import/history`);
                if (!active) return;
                const items = data || [];
                setHistory(items);
                const last = items[0];
                setUrl(last?.spreadsheet_url || '');
                if (last?.spreadsheet_url) {
                    await fetchTabs(last.spreadsheet_url, last.tab_name);
                } else {
                    setTabs([]);
                    setTab('');
                }
            } catch (requestError) {
                if (active) setError(errorMessage(requestError));
            }
        };
        load();
        return () => { active = false; };
    }, [budgetId, fetchTabs, isOpen]);

    const refreshHistory = async () => {
        const { data } = await axios.get(`/api/v1/budget/${budgetId}/sheet-import/history`);
        setHistory(data || []);
    };

    const loadTabs = async () => {
        if (!url.trim()) {
            setError('Enter a Google Sheets URL first.');
            return;
        }
        await fetchTabs(url.trim());
    };

    const runPreview = async () => {
        setLoadingPreview(true);
        setError('');
        setApplyResult(null);
        setBudgetRefreshStatus(null);
        setAcknowledgeBlankCells(false);
        try {
            const { data } = await axios.post(`/api/v1/budget/${budgetId}/sheet-import/preview`, {
                url: url.trim(),
                tab,
            });
            setPreview(data);
            await refreshHistory();
        } catch (requestError) {
            setError(errorMessage(requestError));
        } finally {
            setLoadingPreview(false);
        }
    };

    const apply = async () => {
        if (!preview?.receipt_id || applyResult) return;
        setApplying(true);
        setError('');
        try {
            const { data } = await axios.post(
                `/api/v1/budget/${budgetId}/sheet-import/${preview.receipt_id}/apply`,
                {
                    acknowledgeBlankCells,
                    ...(Number(preview.closed_forecast_differences?.cell_count || 0) > 0 ? {
                        amend_closed_forecast: {
                            months: (preview.closed_forecast_differences?.months || []).map((item) => item.month),
                            reason: `Workbook forecast sync: ${preview.source?.tab_name} (preview #${preview.receipt_id})`,
                        },
                    } : {}),
                },
            );
            setApplyResult(data);
            setBudgetRefreshStatus('refreshing');
            await refreshHistory().catch(() => null);
            try {
                await onApplied?.(data);
                setBudgetRefreshStatus('refreshed');
            } catch {
                setBudgetRefreshStatus('failed');
            }
        } catch (requestError) {
            setError(errorMessage(requestError));
        } finally {
            setApplying(false);
        }
    };

    const visibleAccounts = useMemo(() => {
        const accounts = preview?.accounts || [];
        if (!changedOnly) return accounts;
        return accounts.filter((account) => account.values.some((cell) => (
            cell.status === 'update'
            || cell.status === 'derived_from_demand_plan'
            || (cell.status === 'frozen' && Math.abs(Number(cell.delta || 0)) > 0.000001)
        )));
    }, [preview, changedOnly]);

    if (!isOpen) return null;
    const blankCells = preview?.blank_cells || [];
    const calculatedInBudgetReasons = (preview?.reasons || [])
        .filter((reason) => reason.code === 'calculated_in_budget');
    const keptNotInSheet = preview?.kept_not_in_sheet || [];
    const grossFullYear = preview?.gross_vs_sheet?.full_year;
    const applySummary = applyResult ? summarizeAppliedImport(preview, applyResult) : null;
    const closedAmendmentSummary = applyResult
        ? summarizeClosedForecastAmendment(applyResult)
        : null;
    const closedForecastDifferences = preview?.closed_forecast_differences;
    const closedForecastCellCount = Number(closedForecastDifferences?.cell_count || 0);
    const closedForecastApplyNote = closedForecastCellCount > 0
        ? `Includes ${closedForecastCellCount} closed-month forecast cell${closedForecastCellCount === 1 ? '' : 's'}`
        : null;
    const dependency = preview?.dependency;
    const demandSheet = dependency?.demand_sheet;
    const linkedDemandPlanId = dependency?.demand_plan?.id;
    const demandPlanLinkRequired = demandSheet?.status === 'differs';
    const demandSheetStatus = {
        in_sync: 'In sync',
        differs: 'Different from the Demand Plan',
        invalid: 'Needs correction',
        unreadable: 'Could not read the sheet',
        not_linked: 'No linked sheet',
        unavailable: 'Could not check the sheet',
    }[demandSheet?.status] || 'Unavailable';
    const readbackStatus = applySummary?.readbackStatus || 'UNKNOWN';
    const readbackPassed = readbackStatus === 'PASS';
    const changedAmounts = applySummary
        ? `${applySummary.changedCount} ${applySummary.changedCount === 1 ? 'amount' : 'amounts'} changed${applySummary.closedForecastChangedCount > 0
            ? ` (${applySummary.closedForecastChangedCount} in closed-month forecast)`
            : ''}`
        : '';
    const appliedSummaryDetails = applySummary ? [
        `Open months updated: ${monthList(applySummary.appliedMonths)}`,
        applySummary.cellsWritten == null ? null : `${applySummary.cellsWritten} cells written`,
        changedAmounts,
    ].filter(Boolean).join(' · ') : '';
    const parity = applyResult?.readback?.parity;
    const parityLabel = {
        complete: 'Complete parity',
        partial: 'Partial import',
        stale_dependency: 'Stale dependency',
    }[parity?.status] || 'Parity unavailable';
    const dependencyStepStatus = (step) => {
        if (step.key === 'demand_import' && demandSheet?.status === 'unavailable') return 'Could not check';
        if (step.key === 'budget_refresh' && dependency.budget_refresh?.status === 'unavailable') return 'Could not check';
        return step.required ? 'Required' : 'Not needed';
    };
    const canApplyPreview = canEdit
        && preview?.status === 'ok'
        && Boolean(preview?.receipt_id)
        && !applyResult
        && (!blankCells.length || acknowledgeBlankCells);

    return (
        <SheetImportOverlay>
            <div role="dialog" aria-modal="true" aria-labelledby="budget-sheet-import-title" className="flex h-[94dvh] w-[min(98vw,1600px)] flex-col rounded-lg bg-white shadow-xl">
                <div className="flex shrink-0 items-start justify-between border-b p-4 sm:p-5">
                    <div>
                        <h2 id="budget-sheet-import-title" className="text-xl font-semibold">Import Budget from Google Sheet</h2>
                        <p className="mt-1 text-sm text-gray-600">Apply updates the forecast in every month to match the sheet. Closed-month actuals and the closed-period snapshots stay unchanged; gross stays on the demand plan.</p>
                    </div>
                    <button type="button" aria-label="Close budget sheet import" onClick={onClose} className="rounded px-2 py-1 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">Close</button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto p-4 pb-8 sm:p-5 sm:pb-8">

                {error && <div role="alert" className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

                {applyResult && applySummary && (
                    <SheetImportSuccessBanner forwardedRef={applySummaryRef} tone={readbackPassed ? 'success' : 'error'}>
                        <h3 className="font-semibold">
                            Import applied · receipt #{applySummary.receiptId} · Readback {readbackStatus}
                        </h3>
                        <div className="mt-1 text-xs">{appliedSummaryDetails}</div>
                        {closedAmendmentSummary && (
                            <div className="mt-1 text-xs">
                                Closed-month forecast amended: {monthList(closedAmendmentSummary.months)} · NI {amount(closedAmendmentSummary.netIncomeBefore)} → {amount(closedAmendmentSummary.netIncomeAfter)}
                            </div>
                        )}
                        {applySummary.changedCount === 0 && (
                            <div className="mt-1 text-xs">The sheet's amounts already matched this budget, so no numbers changed.</div>
                        )}
                        {applySummary.frozenMonths.length > 0 && (
                            <div className="mt-1 text-xs">
                                Closed months kept as actuals, not changed: {monthList(applySummary.frozenMonths)}
                            </div>
                        )}
                        {applySummary.demandPlanGross && (
                            <div className="mt-1 text-xs">Gross Sales still comes from the Demand Plan.</div>
                        )}
                        {applyResult.refresh?.ran && (
                            <div className="mt-1 text-xs">
                                Gross refreshed from demand plan: {(applyResult.refresh.gross_changes || []).length} cells
                            </div>
                        )}
                        {parity && (
                            <div className="mt-2">
                                <div className={`inline-flex rounded px-2 py-1 text-xs font-semibold ${parity.status === 'complete'
                                    ? 'bg-green-200 text-green-900'
                                    : parity.status === 'stale_dependency'
                                        ? 'bg-red-200 text-red-900'
                                        : 'bg-amber-200 text-amber-900'}`}
                                >
                                    {parityLabel}
                                </div>
                                {(parity.reasons || []).length > 0 && (
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-xs">
                                        {parity.reasons.map((reason, index) => <li key={`${reason}-${index}`}>{reason}</li>)}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div className="mt-1 text-xs">
                            {budgetRefreshStatus === 'refreshed'
                                ? 'Budget page refreshed.'
                                : budgetRefreshStatus === 'failed'
                                    ? "Applied, but the budget page didn't refresh; reload the page."
                                    : 'Refreshing budget page…'}
                        </div>
                    </SheetImportSuccessBanner>
                )}

                <SheetImportUrlTabs
                    url={url}
                    onUrlChange={(value) => {
                        setUrl(value);
                        setTabs([]);
                        setTab('');
                        setPreview(null);
                    }}
                    tabs={tabs}
                    tab={tab}
                    onTabChange={setTab}
                    onLoadTabs={loadTabs}
                    loadingTabs={loadingTabs}
                    onPreview={runPreview}
                    loadingPreview={loadingPreview}
                    canPreview={Boolean(url && tab)}
                />

                {preview && (
                    <div className="mt-5 space-y-4">
                        <div className={`rounded border px-3 py-2 text-sm ${preview.status === 'ok' ? 'border-green-200 bg-green-50 text-green-900' : 'border-red-200 bg-red-50 text-red-900'}`}>
                            <div className="font-semibold">Preview {preview.status === 'ok' ? 'ready' : 'rejected'}</div>
                            <div className="mt-1 text-xs">
                                {preview.source?.tab_name} · GID {preview.source?.sheet_gid} · read {readDateTime(preview.source?.source_read_at)}
                            </div>
                        </div>

                        {!applyResult && closedForecastCellCount > 0 && (
                            <section className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                                <h3 className="font-semibold">Forecast updates in closed months</h3>
                                <p className="mt-1 text-xs">
                                    {closedForecastCellCount} saved forecast cells in {monthList(closedForecastDifferences.months?.map((item) => item.month))} will be updated to the sheet: NI {signedAmount(closedForecastDifferences.net_income_delta)}. Actuals (QuickBooks) for these months are not changed.
                                </p>
                                <table className="mt-2 w-full text-left text-xs">
                                    <thead>
                                        <tr>
                                            <th className="py-1 pr-3">Closed month</th>
                                            <th className="py-1 pr-3">Cells that differ</th>
                                            <th className="py-1">Net Income difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {closedForecastDifferences.months.map((item) => (
                                            <tr key={item.month} className="border-t border-blue-200">
                                                <td className="py-1 pr-3">{dateLabel(item.month)}</td>
                                                <td className="py-1 pr-3">{item.cells}</td>
                                                <td className="py-1">{signedAmount(item.net_income_delta)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        )}

                        {dependency && (
                            <section className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                                <h3 className="font-semibold">Demand source</h3>
                                <div className="mt-1 text-xs">
                                    <div>Plan: {dependency.demand_plan?.name || dependency.demand_plan?.error || 'Unavailable'}</div>
                                    {dependency.demand_plan?.updated_at && (
                                        <div>Plan updated: {date(dependency.demand_plan.updated_at)}</div>
                                    )}
                                    <div>
                                        Linked sheet:{' '}
                                        {demandSheet?.spreadsheet_url
                                            ? <a className="underline" href={demandSheet.spreadsheet_url} target="_blank" rel="noreferrer">{demandSheet.tab_name || 'Open sheet'}</a>
                                            : (demandSheet?.tab_name || 'No sheet linked')}
                                        {demandSheet?.latest_applied_receipt
                                            ? ` · Last import: receipt #${demandSheet.latest_applied_receipt.id} · ${date(demandSheet.latest_applied_receipt.applied_at || demandSheet.latest_applied_receipt.updated_at)}`
                                            : ' · No applied import yet'}
                                    </div>
                                    <div>Sheet status: <span className="font-semibold">{demandSheetStatus}</span></div>
                                    {demandSheet?.status === 'invalid' && (demandSheet.reasons || []).length > 0 && (
                                        <div className="mt-1">{demandSheet.reasons.join(' ')}</div>
                                    )}
                                    {demandSheet?.status === 'unreadable' && (
                                        <div className="mt-1">{demandSheet.error || (demandSheet.reasons || []).join(' ')}</div>
                                    )}
                                    {demandSheet?.status === 'unavailable' && (
                                        <div className="mt-1">{demandSheet.error}</div>
                                    )}
                                </div>
                                <ol className="mt-2 space-y-1 text-xs">
                                    {(dependency.steps || []).map((step, index) => (
                                        <li key={step.key}>
                                            {index + 1}. {step.label} · {dependencyStepStatus(step)}
                                        </li>
                                    ))}
                                </ol>
                                {dependency.budget_refresh?.status === 'unavailable' && (
                                    <p className="mt-2 text-xs">Budget refresh could not be checked: {dependency.budget_refresh.error}</p>
                                )}
                                {(dependency.budget_refresh?.gross_changes || []).length > 0 && (
                                    <div className="mt-2 text-xs">
                                        <div className="font-semibold">Open-month Gross Sales changes</div>
                                        <ul className="mt-1 space-y-1">
                                            {dependency.budget_refresh.gross_changes.map((change) => (
                                                <li key={`${change.account_number}-${change.month}`}>
                                                    {change.account_number} · {dateLabel(change.month)} · {amount(change.before)} → {amount(change.after)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(dependency.closed_months || []).length > 0 && (
                                    <p className="mt-2 text-xs">
                                        Closed months kept as closed: {monthList(dependency.closed_months)}
                                    </p>
                                )}
                                {demandPlanLinkRequired && linkedDemandPlanId != null && (
                                    <div className="mt-2">
                                        <ul className="space-y-1 text-xs">
                                            {demandSheet?.changes?.target && (
                                                <li>Annual target: {amount(demandSheet.changes.target.from)} → {amount(demandSheet.changes.target.to)}</li>
                                            )}
                                            {(demandSheet?.changes?.channels || []).map((change) => (
                                                <li key={`channel-${change.channel}`}>
                                                    {change.channel} split: {percent(change.from)} → {percent(change.to)}
                                                </li>
                                            ))}
                                            {(demandSheet?.changes?.months || []).map((change) => (
                                                <li key={`month-${change.month}`}>
                                                    {dateLabel(change.month)} allocation: {percent(change.from)} → {percent(change.to)}
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            type="button"
                                            onClick={() => navigate(
                                                `${ROUTES.DEMAND_PLANNING}?id=${encodeURIComponent(linkedDemandPlanId)}&import=sheet`
                                            )}
                                            className="mt-2 rounded border border-blue-700 px-3 py-1.5 text-xs font-medium text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            Open Forecast Demand import
                                        </button>
                                    </div>
                                )}
                            </section>
                        )}

                        {preview.structure_mismatch && (
                            <div role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                <p>This budget's accounts and this tab don't line up. Fix the accounts listed under Import blockers. Nothing was changed.</p>
                                {calculatedInBudgetReasons.length > 0 && (
                                    <ul className="mt-2 space-y-1 text-xs">
                                        {calculatedInBudgetReasons.map((reason, index) => (
                                            <li key={`${reason.account_number}-${index}`}>
                                                {describeImportReason(reason)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {(preview.reasons || []).length > 0 && (
                            <section className="rounded border border-red-200 p-3">
                                <h3 className="mb-2 text-sm font-semibold text-red-800">Import blockers</h3>
                                <ul className="space-y-1 text-xs text-red-800">
                                    {preview.reasons.map((reason, index) => (
                                        <li key={`${reason.code}-${index}`}>
                                            {describeImportReason(reason)}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {keptNotInSheet.length > 0 && (
                            <section className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                                <h3 className="mb-2 font-semibold">Budget accounts not in this tab</h3>
                                <ul className="space-y-1 text-xs">
                                    {keptNotInSheet.map((account) => (
                                        <li key={account.line_id}>
                                            Not in this tab, Eadin keeps its current values: {account.account_number} {account.account_name} ($0 in open months; closed months unchanged).
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {blankCells.length > 0 && (
                            <section className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                <div>{blankCells.length} blank mapped cells will keep their current Budget value (not written).</div>
                                <ul className="mt-2 grid max-h-24 grid-cols-2 gap-x-4 overflow-auto text-xs md:grid-cols-4">
                                    {blankCells.map((cell) => (
                                        <li key={`${cell.account_number}-${cell.month}`}>
                                            {cell.account_number} · {dateLabel(cell.month)}
                                            {cell.current == null ? '' : ` · Current ${amount(cell.current)}`}
                                        </li>
                                    ))}
                                </ul>
                                <label className="mt-2 flex items-center gap-2 text-xs font-medium">
                                    <input type="checkbox" checked={acknowledgeBlankCells} onChange={(event) => setAcknowledgeBlankCells(event.target.checked)} />
                                    Acknowledge blank cells — keep current Budget values
                                </label>
                            </section>
                        )}

                        <section className="grid gap-3 md:grid-cols-4">
                            {[
                                ['Gross', preview.totals?.gross],
                                ['Net Income', preview.totals?.net_income],
                            ].map(([label, value]) => (
                                <div key={label} className="rounded border p-3 text-sm">
                                    <div className="text-xs uppercase text-gray-500">{label}</div>
                                    <div className="mt-1 font-semibold">{amount(value?.current)} → {amount(value?.source)}</div>
                                    <div className="text-xs text-gray-600">Delta {amount(value?.delta)}</div>
                                </div>
                            ))}
                            <div className="rounded border p-3 text-sm">
                                <div className="text-xs uppercase text-gray-500">Open-month gross delta</div>
                                <div className="mt-1 font-semibold">{amount(preview.totals?.open_month_gross_delta)}</div>
                            </div>
                            <div className="rounded border p-3 text-sm">
                                <div className="text-xs uppercase text-gray-500">Open-month Net Income delta</div>
                                <div className="mt-1 font-semibold">{amount(preview.totals?.open_month_net_income_delta)}</div>
                            </div>
                        </section>
                        {(preview.informational_unmapped_sheet_accounts || []).length > 0 && (
                            <section className="rounded border p-3">
                                <h3 className="mb-2 text-sm font-semibold">Unmapped zero/blank rows · informational</h3>
                                <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                                    {preview.informational_unmapped_sheet_accounts.map((item, index) => (
                                        <span key={`${item.account_number}-${item.row}-${index}`} className="rounded bg-gray-100 px-2 py-1">
                                            {unmappedSheetAccountLabel(item)} · row {item.row}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {preview.demand_plan_gross_note && (
                            <section className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                                <p>{preview.demand_plan_gross_note}</p>
                                {grossFullYear && (
                                    <p className="mt-1 text-xs">
                                        {Math.abs(Number(grossFullYear.delta || 0)) < 0.005
                                            ? `For the full year, the sheet's Gross Sales matches Eadin at ${amount(grossFullYear.sheet)}.`
                                            : `For the full year, the sheet shows ${amount(grossFullYear.sheet)} in Gross Sales, ${amount(Math.abs(grossFullYear.delta))} ${grossFullYear.delta > 0 ? 'more' : 'less'} than Eadin's ${amount(grossFullYear.budget)} from the linked Demand Plan.`}
                                    </p>
                                )}
                            </section>
                        )}

                        {preview.driver_basis?.saved_driver_versions === 0 && (
                            <section className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                This budget has no saved driver rates. Discounts, refunds, rebates and cost lines import as the sheet's dollar amounts and won't follow Demand Plan gross until driver rates are saved.
                            </section>
                        )}

                        <section className="rounded border p-3">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <h3 className="text-sm font-semibold">Account and month preview</h3>
                                <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={changedOnly} onChange={(event) => setChangedOnly(event.target.checked)} />
                                    Changed only
                                </label>
                            </div>
                            <div className="max-h-[60dvh] max-w-full overflow-auto">
                                <table className="min-w-[2040px] w-full table-fixed border-collapse text-left text-xs">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="sticky left-0 top-0 z-30 w-[240px] min-w-[240px] whitespace-normal break-words border-b bg-gray-50 p-2">Account</th>
                                            {(preview.months || []).map((month) => <th key={month} className="sticky top-0 z-10 w-[150px] min-w-[150px] whitespace-normal break-words border-b bg-gray-50 p-2 text-right tabular-nums">{dateLabel(month)}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleAccounts.map((account) => (
                                            <tr key={account.account_number} className="border-b align-top">
                                                <td className="sticky left-0 z-20 w-[240px] min-w-[240px] whitespace-normal break-words bg-white p-2 font-medium">{account.account_number} · {account.account_name}</td>
                                                {account.values.map((cell) => (
                                                    <td key={cell.month} className={`min-w-[150px] whitespace-normal break-words p-2 text-right tabular-nums ${cell.status === 'frozen' ? 'bg-gray-50 text-gray-500' : cell.status === 'derived_from_demand_plan' ? 'bg-white text-blue-800' : ''}`}>
                                                        <div className="font-medium">
                                                            {amount(cell.status === 'frozen' || cell.status === 'keep' || cell.status === 'derived_from_demand_plan' || cell.preserved ? cell.current : cell.source)}
                                                        </div>
                                                        {cell.status === 'update' && (
                                                            <div className="text-[10px]">
                                                                was {amount(cell.current)} · change {Number(cell.delta) >= 0 ? '+' : ''}{amount(cell.delta)}
                                                            </div>
                                                        )}
                                                        {cell.status === 'frozen' && <div className="text-[10px]">Closed · not changed</div>}
                                                        {cell.status === 'keep' && <div className="text-[10px]">Keep current</div>}
                                                        {cell.status === 'derived_from_demand_plan' && <div className="text-[10px]">From Demand Plan</div>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <div className="grid gap-4 lg:grid-cols-2">
                            <section className="rounded border p-3">
                                <h3 className="text-sm font-semibold">Sheet subtotal checks (±$1)</h3>
                                <p className="mb-2 mt-1 text-xs text-gray-600">Checks that this tab's rows add up to its own Shopify net, Amazon net, total net and Net Income.</p>
                                <div className="max-h-44 overflow-auto text-xs">
                                    {(preview.metric_checks || []).map((check) => (
                                        <div key={`${check.metric}-${check.month}`} className={`flex justify-between gap-3 border-b py-1 ${check.ok ? 'text-gray-700' : 'text-red-700'}`}>
                                            <span>{check.metric} · {dateLabel(check.month)}</span>
                                            <span>Rows {amount(check.expected)} · subtotal {amount(check.actual)} · {check.ok ? 'PASS' : 'FAIL'}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                            <section className="rounded border p-3">
                                <h3 className="mb-2 text-sm font-semibold">Implied rates · source vs current Budget account ratios</h3>
                                <div className="max-h-44 overflow-auto text-xs">
                                    {(preview.implied_rates || []).map((rate) => (
                                        <div key={rate.month} className="border-b py-1">
                                            <span className="font-medium">{dateLabel(rate.month)}</span>
                                            <span> · Discount {rate.discount == null ? '—' : `${(rate.discount * 100).toFixed(2)}%`} / {rate.current_budget.discount == null ? '—' : `${(rate.current_budget.discount * 100).toFixed(2)}%`}</span>
                                            <span> · Rebate {rate.rebate == null ? '—' : `${(rate.rebate * 100).toFixed(2)}%`} / {rate.current_budget.rebate == null ? '—' : `${(rate.current_budget.rebate * 100).toFixed(2)}%`}</span>
                                            <span> · Shopify refund {rate.shopify_refund == null ? '—' : `${(rate.shopify_refund * 100).toFixed(2)}%`} / {rate.current_budget.shopify_refund == null ? '—' : `${(rate.current_budget.shopify_refund * 100).toFixed(2)}%`}</span>
                                            <span> · Amazon refund {rate.amazon_refund == null ? '—' : `${(rate.amazon_refund * 100).toFixed(2)}%`} / {rate.current_budget.amazon_refund == null ? '—' : `${(rate.current_budget.amazon_refund * 100).toFixed(2)}%`}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>

                        <section className="rounded border p-3">
                            <h3 className="mb-2 text-sm font-semibold">Month status</h3>
                            <div className="flex flex-wrap gap-2 text-xs">
                                {(preview.modes || []).map((mode) => (
                                    <span key={mode.month} className="rounded bg-gray-100 px-2 py-1">
                                        {dateLabel(mode.month)} · Sheet: {mode.mode || '—'} · Eadin: {mode.eadin_status === 'closed' ? 'Actual (closed)' : 'Forecast (open)'}
                                    </span>
                                ))}
                            </div>
                        </section>

                    </div>
                )}

                <SheetImportHistory
                    items={history}
                    renderItem={(item) => (
                            <div key={item.id} className="flex flex-wrap justify-between gap-2 border-b py-1">
                                <span>#{item.id} · {item.status} · {item.tab_name}</span>
                                <span>
                                    {date(item.created_at)} · {item.readback?.status || 'No readback'}
                                    {item.months_applied?.length ? ` · applied ${monthList(item.months_applied)}` : ''}
                                    {item.months_frozen?.length ? ` · frozen ${monthList(item.months_frozen)}` : ''}
                                </span>
                            </div>
                    )}
                />

                </div>
                <SheetImportFooter
                    applied={Boolean(applyResult)}
                    successLabel={`Receipt #${applySummary?.receiptId} · Readback ${readbackStatus}`}
                    successTone={readbackPassed ? 'success' : 'error'}
                    onClose={onClose}
                    canEdit={canEdit && Boolean(preview)}
                    canApply={canApplyPreview}
                    applying={applying}
                    onApply={apply}
                    applyLabel={demandPlanLinkRequired ? 'Apply (demand plan still differs)' : 'Apply import'}
                    note={closedForecastApplyNote}
                    closeAriaLabel="Close budget sheet import"
                />
            </div>
        </SheetImportOverlay>
    );
};

export default BudgetSheetImportModal;
